import classes from './AnnotationJobData.module.scss'
import {useParams} from "react-router-dom";
import {useMutation, useQuery, useQueryClient} from "react-query";
import QueryKeys from "../../../../../../react-query/QueryKeys";
import {
    getAnnotationClasses,
    getAnnotationJobData,
    getAnnotationOrderDefinition,
    updateAnnotationTag
} from "../../../../../../_metronic/helpers/backend_helper";
import React, {useCallback, useEffect, useState} from "react";
import {
    AnnotationClassesListResponse,
    AnnotationSegmentListResponse,
    AnnotationSegmentResponse,
    UpdateAnnotationRequest
} from "../../../../../../AUTO_GENERATED_TYPES";
import {Card} from "reactstrap";
import TaggingTooltip from "../../../../Community/Components/TaggingTooltip";
import SegmentDetailsPagination from "../../../../Community/Components/SegmentDetailsPagination";
import AnnotationTag from "../AnnotationTag/AnnotationTag";
import AnnotationDetailsModal from "../AnnotationDetailsModal/AnnotationDetailsModal";
import {toast} from "react-toastify";
import {debounce} from "lodash";
import {KTSVG} from "../../../../../../_metronic/helpers";
import useAnnotation from "../../../../../../store/annotation/useAnnotation";
import AnnotationZoomModal from "../AnnotationZoomModal/AnnotationZoomModal";
import { useNavigate } from "react-router-dom";
import VideoModal from '../../../../../Components/UI/VideoModal';
import PlotlyOrImage from '../../../../../Components/UI/PlotlyOrImage';


const AnnotationJobData = () => {
    const { id } = useParams();
    const annotationId = Number(id);
    const queryClient = useQueryClient();

    const {
        data: { dataRequest, tagFilter, page, itemsPerPage, imageType, sortCount, showDivider },
        functions: { changePage }
    } = useAnnotation();

    const showVideoGeneratorIcon = imageType.value === 'path_2d_motion_plot';

    const navigate = useNavigate();

    const [modalContext, setModalContext] = useState<AnnotationSegmentResponse | null>(null);
    const [zoomContext, setZoomContext] = useState<{ show: boolean, segment?: AnnotationSegmentResponse, index?: number }>({ show: false });
    const [tagUpdates, setTagUpdates] = useState<UpdateAnnotationRequest[]>([]);
    const [videoModalContext, setVideoModalContext] = useState<{ seg_id: string, creation_time: string, screen_width: number, screen_height: number } | null>(null);
    const [showVideoModal, setShowVideoModal] = useState(false);
    
    const { data, isLoading } = useQuery(
        [QueryKeys.Annotation, QueryKeys.AnnotationJobData, annotationId, dataRequest, tagFilter, itemsPerPage, page],
        () => getAnnotationJobData(annotationId, dataRequest, tagFilter, itemsPerPage, page),
        {
            retry: false,
            refetchOnWindowFocus: false
        }
    );

    const { data: annotationClasses, isLoading: isClassesLoading } = useQuery(
        [QueryKeys.AnnotationJobClasses, annotationId],
        () => getAnnotationClasses(annotationId),
        {
            retry: false,
            staleTime: 5 * (60 * 1000)
        }
    );

    const { data: sortDefinitions, isLoading: isSortDefinitionsLoading } = useQuery(
        [QueryKeys.Annotation, QueryKeys.AnnotationOrderByDefinition, annotationId],
        () => getAnnotationOrderDefinition(annotationId),
        {
            retry: false,
            staleTime: 5 * (60 * 1000)
        }
    );

    const debouncedHeaderInvalidation = useCallback(debounce(() => {
        setTagUpdates([])
        queryClient.invalidateQueries([QueryKeys.Annotation, QueryKeys.AnnotationJobHeader])
        toast.success('Tags Updated!', {autoClose: 1000})
    }, 3000), [])

    const debouncedTagging = useCallback(debounce((tags: UpdateAnnotationRequest[], requestBody: UpdateAnnotationRequest[]) => {
        const oldCopy = [...tags].filter(tag => tag.seg_id_list[0] !== requestBody[0].seg_id_list[0])
        const newRequest = [...oldCopy, ...requestBody]

        return updateAnnotationTag(annotationId, {update_list: newRequest})
    }, 3000), [])

    const {
        mutate: sendTagRequest,
        reset: cancelTagRequests
    } = useMutation<any, unknown, Array<UpdateAnnotationRequest>, () => void>((requestBody) => {
        const segmentIndex = data!.segments!.findIndex(value => value.id === requestBody[0].seg_id_list[0])
        const deepCopyData = JSON.parse(JSON.stringify(data)) as AnnotationSegmentListResponse
        const classes = queryClient.getQueryData<AnnotationClassesListResponse>([QueryKeys.AnnotationJobClasses, annotationId])


        const newTag = requestBody[0].segment_tag
        deepCopyData.segments![segmentIndex].tag = newTag

        const oldName = deepCopyData.segments![segmentIndex].tag_name
        deepCopyData.segments![segmentIndex].tag_name = classes?.classes.find(segClass => segClass.class_code === newTag)?.class_name ?? oldName

        queryClient.setQueryData([QueryKeys.Annotation, QueryKeys.AnnotationJobData, annotationId, dataRequest, tagFilter, itemsPerPage, page], deepCopyData)

        setTagUpdates(prevState => {
            const oldCopy = [...prevState].filter(tag => tag.seg_id_list[0] !== requestBody[0].seg_id_list[0])
            return [...oldCopy, ...requestBody]
        })

        return debouncedTagging(tagUpdates, requestBody) as any
    }, {
        onMutate: (variables => {
            return () => {
                toast.error(`Something went wrong, rolling back changes for Tag id: ${variables[0].seg_id_list[0]}`)
                queryClient.setQueryData([QueryKeys.Annotation, QueryKeys.AnnotationJobData, annotationId, dataRequest, tagFilter, itemsPerPage, page], data)
            }
        }),
        onSuccess: debouncedHeaderInvalidation,
        onError: (error, variables, context) => {
            if (context) {
                context()
                setTagUpdates([])
            }
        }
    })

    // Helper function to get the sorted field value from a segment
    const getSortedFieldValue = (segment: AnnotationSegmentResponse) => {
        if (!dataRequest.orders || dataRequest.orders.length === 0 || !sortDefinitions?.orders) {
            return null;
        }
        // Get the first sort order (primary sort)
        const primarySort = dataRequest.orders[0];
        const orderDefinitionId = primarySort.order_definition_id;
        
        // Find the sort definition to get the field name
        const sortDefinition = sortDefinitions.orders.find(order => order.id === orderDefinitionId);
        if (!sortDefinition) {
            return null;
        }
        
        // Map the short_name to the actual field in the segment
        const fieldMapping: { [key: string]: keyof AnnotationSegmentResponse } = {
            'segment_id': 'seg_id',
            'community_index': 'comm_idx',
            'count_seq_motion': 'cnt_seq_motion',
            'count_unique_xy': 'cnt_unique_xy',
            'count_segments': 'cnt_segments',
            'count_seq_acc': 'cnt_seq_acc',
            'count_motion_down': 'cnt_motion_down',
            'count_motion_move': 'cnt_motion_move',
            'app_version_code': 'app_version_code_str',
            'country': 'country_code',
            'segment_name': 'seg_name'
        };
        
        const formattedShortName = sortDefinition.short_name.replaceAll(' ', '_').toLowerCase();
        const fieldKey = fieldMapping[formattedShortName] || formattedShortName;
        if (fieldKey === undefined || fieldKey === null) {
            return null;
        }
        return {
            fieldName: sortDefinition.short_name,
            value: segment[fieldKey]
        };
    };

    // Helper function to format the sorted field value for display
    const formatSortedFieldValue = (fieldData: { fieldName: string, value: any } | null): string => {
        if (!fieldData) {
            return '';
        }
        
        const { fieldName, value } = fieldData;
        
        if (value === null || value === undefined) {
            return `${fieldName}: N/A`;
        }
        
        if (Array.isArray(value)) {
            return `${fieldName}: ${value.join(', ')}`;
        }
        
        if (typeof value === 'object') {
            return `${fieldName}: ${JSON.stringify(value)}`;
        }
        
        return `${fieldName}: ${String(value)}`;
    };

    /*
    * This function is used to determine if a separator should be shown between two segments(images).
    * It is used to separate segments that have different values for the sorted field.
    * The sorting must be applied to the segments.
    */
    const shouldShowSeparator = (currentSegment: AnnotationSegmentResponse, previousSegment: AnnotationSegmentResponse | null) => {
        // Only show separators when sorting is applied AND divider toggle is ON
        if (sortCount === 0 || !dataRequest.orders || dataRequest.orders.length === 0 || !showDivider) {
            return false;
        }
        
        // Show separator for the first segment
        if (!previousSegment) {
            return true;
        }
        
        const currentFieldData = getSortedFieldValue(currentSegment);
        const previousFieldData = getSortedFieldValue(previousSegment);
        
        if (!currentFieldData || !previousFieldData) {
            return false;
        }
        
        // If the sorted field value is an object, compare the JSON strings
        if (typeof(currentFieldData.value) === 'object' && typeof(previousFieldData.value) === 'object') {
            return JSON.stringify(currentFieldData.value) !== JSON.stringify(previousFieldData.value);
        }

        return currentFieldData.value !== previousFieldData.value;
    };

    if (isLoading || isClassesLoading || isSortDefinitionsLoading || !data) {
        return null
    }

    const tagHandler = (requestBody: UpdateAnnotationRequest[]) => {
        cancelTagRequests()
        sendTagRequest(requestBody)
    }


    const openModalHandler = (segment?: AnnotationSegmentResponse) => {
        if (segment) {
            setModalContext(segment)
        }
    }

    const generateVideo = (segment?: AnnotationSegmentResponse) => {
        if (segment) {
            let creation_time = '';
            const plotUrl = segment.plots?.['path_2d_motion_plot'];
            if (plotUrl) {
                // Extract the datetime part between '/plots/' and the next '/'
                const match = plotUrl.match(/plots\/([^/]+)\//);
                if (match && match[1]) {
                    creation_time = match[1];
                }
            }
            const videoSegment = {
                seg_id: segment.seg_id.toString(),
                screen_width: segment.screen_width,
                screen_height: segment.screen_height,
                creation_time
            }
            setVideoModalContext(videoSegment)
            setShowVideoModal(true)
        }
    }

    const toggleZoom = (segment?: AnnotationSegmentResponse | React.KeyboardEventHandler<any> | React.MouseEventHandler<any> | undefined) => {
        const argIsSeg = (seg: any): seg is AnnotationSegmentResponse => {
            return !!seg?.tag;
        }

        setZoomContext(prevState => {
            if (argIsSeg(segment)) {
                const annotationIndex = (JSON.parse(JSON.stringify(queryClient.getQueryData([QueryKeys.Annotation, QueryKeys.AnnotationJobData, annotationId, dataRequest, tagFilter, itemsPerPage, page]))) as AnnotationSegmentListResponse)?.segments?.findIndex((seg => seg.id === segment?.id))

                return {show: true, segment, index: annotationIndex}
            }

            if (prevState.show) {
                return {show: false}
            }

            return {show: false}
        })
    }

    const showZoomModal = (segment?: AnnotationSegmentResponse) => {
        setModalContext(null);
        const annotationIndex = (JSON.parse(JSON.stringify(queryClient.getQueryData([QueryKeys.Annotation, QueryKeys.AnnotationJobData, annotationId, dataRequest, tagFilter, itemsPerPage, page]))) as AnnotationSegmentListResponse)?.segments?.findIndex((seg => seg.id === segment?.id))
        setZoomContext({
            show: true,
            segment: segment,
            index: annotationIndex
        });
    };

    const closeModalHandler = () => setModalContext(null)

    const changePageHandler = (newPage: number) => {
        const queryParams = new URLSearchParams(window.location.search);
        queryParams.set("segpage", newPage.toString());
        navigate(`?${queryParams.toString()}`, { replace: true });
        changePage(newPage - 1);
    };

    return <>
        <Card className={classes.container}>
            <div className={classes.images}>
                {(data?.segments && data?.segments?.length > 0) ?
                    data.segments?.map((segment, index) => {
                        const showSeparator = shouldShowSeparator(segment, index > 0 ? data.segments![index - 1] : null);
                        let separatorText = '';
                        if (showSeparator) {
                            const sortedFieldData = getSortedFieldValue(segment);
                            separatorText = formatSortedFieldValue(sortedFieldData);
                        }
                        
                        return (
                            <React.Fragment key={index}>
                                {showSeparator && <div className={classes['sort-separator']}>
                                    <div className={classes['separator-line']}></div>
                                    <div className={classes['separator-text']}>
                                        {separatorText}
                                    </div>
                                    <div className={classes['separator-line']}></div>
                                </div>}
                                <div className={'seg_img_exemple'}>
                                    <TaggingTooltip placement={'auto'}
                                                    tooltip={<>
                                                        <p>Seg Id : <i>{segment.seg_id}</i></p>
                                                        <p>Seg Count : <i>{segment.cnt_segments}</i></p>
                                                        <p>Auth Inference : <i>{segment.auth_inference}</i></p>
                                                        <p>Emu Inference : <i>{segment.emu_inference}</i></p>
                                                        <p>Seg User Id : <i>{segment.user_id}</i></p>
                                                        <p>Seg Session Id : <i>{segment.session_id}</i></p>
                                                        {segment.comment && <p>Comment : <i>{segment.comment}</i></p>}
                                                    </>}
                                    >
                                        <div className={classes.images__image_container}>
                                            <PlotlyOrImage
                                                onClick={toggleZoom.bind(null, segment)}
                                                src={((segment?.plots && imageType.value) ? segment?.plots[imageType.value as keyof typeof segment] : Object.values(segment?.plots ?? {})[0]) as string}
                                                width={500} height={300}
                                                alt="segment_graph"
                                                renderMode="static"/>
                                            <div className={classes.images__tagging}>
                                                {annotationClasses?.classes.map((annotationClass, index) =>
                                                    <AnnotationTag key={index} annotationClass={annotationClass} segId={segment.id}
                                                                isSelected={segment.tag === annotationClass.class_code}
                                                                onClick={tagHandler}/>)}
                                                <span onClick={openModalHandler.bind(null, segment)} className={classes.mag_glass}
                                                >
                                            <KTSVG
                                                path='/media/icons/duotune/general/gen004.svg'
                                                className={'svg-icon-edit svg-icon-2qx cursor-pointer'}
                                            />
                                        </span>
                                        <span style={{ marginLeft: '8px', display: showVideoGeneratorIcon ? 'block' : 'none' }} onClick={generateVideo.bind(null, segment)} className={classes.mag_glass}>
                                            <KTSVG
                                                path='/media/icons/duotune/general/gen063.svg'
                                                className={'svg-icon-edit svg-icon-2qx cursor-pointer'}
                                            />
                                        </span>
                                            </div>
                                        </div>
                                    </TaggingTooltip>
                                </div>
                            </React.Fragment>
                        );
                    }) : <div className={classes.no_results}>No Results Found</div>}
            </div>
            {data?.paging?.total_items ? <SegmentDetailsPagination currentPage={data?.paging?.page ?? 1}
                                                                   pageCount={data?.paging.num_of_pages ?? 1}
                                                                   handlePaginate={(({selected}) => changePage(selected))}/> : null}
        </Card>
        <AnnotationZoomModal showVideoGeneratorIcon={showVideoGeneratorIcon} openModalHandler={openModalHandler} annotationClasses={annotationClasses}
                             setZoomContext={setZoomContext} zoomContext={zoomContext} tagHandler={tagHandler}
                             toggleZoom={toggleZoom} currPage={data?.paging?.page ?? 1} pages={data?.paging.num_of_pages ?? 1} changePage={changePageHandler}/>
        <AnnotationDetailsModal closeHandler={closeModalHandler} segment={modalContext} changeSegment={openModalHandler} showZoomModal={showZoomModal}
                                currPage={data?.paging?.page ?? 1} pages={data?.paging.num_of_pages ?? 1} changePage={changePageHandler}/>

        <VideoModal
            show={showVideoModal}
            onClose={() => setShowVideoModal(false)}
            annotationId={annotationId}
            segmentId={videoModalContext?.seg_id.toString() ?? ''}
            creationTime={videoModalContext?.creation_time ?? ''}
            appToken=''
            scaleFactor={0.5}
            screenWidth={videoModalContext?.screen_width ?? 0}
            screenHeight={videoModalContext?.screen_height ?? 0}
        />
    </>
}

export default AnnotationJobData
